import CampaignConstants from "campaign/util/CampaignConstants";
import { isEmpty, isNil } from "lodash";

const {
  FAVORITE_CAMPAIGN,
  FILTER_STATUS_ACTIVE,
  FILTER_STATUS_ARCHIVED,
  FILTER_STATUS_IDLE,
  FILTER_STATUS_INACTIVE,
  FILTER_STATUS_LIVE,
  FILTER_STATUS_NEW,
  FILTER_STATUS_RUNNING,
} = CampaignConstants;

// Mapping fields for Campaign Pagination, Strategy Search, Filter fields for strategies pagination
export const mapFields = {
  "goalValue.value": "goal_value",
  "healthCheck.spendRatio": "spend_ratio",
  "maxBid.value": "max_bid",
  "minBid.value": "min_bid",
  "pacingAmount.value": "pacing_amount",
  [FAVORITE_CAMPAIGN]: "favorite_campaign",
  advertiserId: "advertiser_id",
  advertiserName: "advertiser_name",
  agencyId: "agency_id",
  agencyName: "agency_name",
  campaignArchived: "campaign_archived",
  campaignCreatedOn: "campaign_created_on",
  campaignDailyPacingAt: "campaign_daily_pacing_at",
  campaignDailyPacingSpendToPace: "campaign_daily_pacing_spend_to_pace",
  campaignDaysLeft: "campaign_days_left",
  campaignEndDate: "campaign_end_date",
  campaignFlightPacingAtPercent: "campaign_flight_pacing_at_percent",
  campaignFlightPacingRemaining: "campaign_flight_pacing_remaining",
  campaignFrequencyAmount: "campaign_frequency_type",
  campaignGoalType: "campaign_goal_type",
  campaignGoalValue: "campaign_goal_value",
  campaignId: "campaign_id",
  campaignImpressionsRemaining: "campaign_impressions_remaining",
  campaignImpressionsToPace: "campaign_impressions_to_pace",
  campaignInFlight: "campaign_in_flight",
  campaignName: "campaign_name",
  campaignSpendIndicatorStatus: "campaign_spend_indicator_status",
  campaignStartDate: "campaign_start_date",
  campaignStatus: "campaign_status",
  campaignTotalBudget: "campaign_total_budget",
  campaignTotalImpressionBudget: "campaign_total_impression_budget",
  campaignUpdatedOn: "campaign_updated_on",
  clicks: "clicks",
  ctc: "ctc",
  ctr: "ctr",
  dynamicBudgetAllocationState: "campaign_dynamic_budget_allocation_type",
  endDate: "strategy_end_date",
  frequencyAmount: "frequency_amount",
  frequencyType: "strategy_frequency_type",
  goalType: "goal_type",
  groupId: "strategy_group_id",
  id: "strategy_id",
  impressionPacingTypeFilter: "strategy_impression_pacing_type",
  impressions: "impressions",
  impressionsYesterday: "impressions_yesterday",
  margin: "margin",
  marginPct: "margin_pct",
  measurabilityRate: "measurability_rate",
  nextMargin: "next_margin",
  nextMarginPct: "next_margin_pct",
  pacingInterval: "pacing_interval",
  pacingType: "pacing_type",
  postClickConversions: "post_click_conversions",
  postClickRevenue: "post_click_revenue",
  postClickTotalSpendRoi: "post_click_total_spend_roi",
  postViewConversions: "post_view_conversions",
  postViewRevenue: "post_view_revenue",
  postViewTotalSpendRoi: "post_view_total_spend_roi",
  startDate: "strategy_start_date",
  status: "strategy_status",
  strategyArchived: "strategy_archived",
  strategyGoalType: "strategy_goal_type",
  strategyGoalValue: "strategy_goal_value",
  strategyInFlight: "strategy_in_flight",
  strategyName: "strategy_name",
  strategyPacingAmount: "strategy_pacing_amount",
  strategyPacingInterval: "strategy_pacing_interval",
  strategyPacingType: "strategy_pacing_type",
  strategySpendIndicatorStatus: "strategy_spend_indicator_status",
  totalConversions: "total_conversions",
  totalRevenue: "total_revenue",
  totalSpend: "total_spend",
  totalSpendCpa: "total_spend_cpa",
  totalSpendCpc: "total_spend_cpc",
  totalSpendCpm: "total_spend_cpm",
  totalSpendRoi: "total_spend_roi",
  totalSpendVcpm: "total_spend_vcpm",
  totalSpendYesterday: "total_spend_yesterday",
  videoCompleteRate: "video_complete_rate",
  videoCompleteRateImpressionBased: "video_complete_rate_impression_based",
  videoFirstQuartileRate: "video_first_quartile_rate",
  videoMidpointRate: "video_midpoint_rate",
  videoPlayRate: "video_play_rate",
  videoThirdQuartileRate: "video_third_quartile_rate",
  viewabilityRate: "viewability_rate",
  viewabilityRate_100Percent: "viewability_rate_100_percent",
};

// Mapping fields for Strategies Pagination for Sorting
// Creating this to avoid messing up the above mapFields const.
export const mapStrategyFields = {
  clicks: "clicks",
  ctc: "ctc",
  ctr: "ctr",
  daysLeft: "strategy_days_left",
  endDate: "strategy_end_date",
  flatRateCpm: "strategy_flat_rate_cpm",
  frequencyAmount: "strategy_frequency_type",
  goalActual: "strategy_goal_actual",
  goalActualFlightToDate: "strategy_goal_actual_ftd",
  goalActualLastSevenDays: "strategy_goal_actual_7_days",
  goalType: "strategy_goal_type",
  goalValue: "strategy_goal_value",
  groupId: "strategy_group_id",
  groupName: "strategy_group_name",
  id: "strategy_id",
  impressionPacingAmount: "strategy_impression_pacing_type",
  impressions: "impressions",
  impressionsYesterday: "impressions_yesterday",
  margin: "margin",
  marginPct: "margin_pct",
  maxBid: "strategy_max_bid",
  measurabilityRate: "measurability_rate",
  minBid: "strategy_min_bid",
  name: "strategy_name",
  nextMargin: "next_margin",
  nextMarginPct: "next_margin_pct",
  pacingAmount: "strategy_pacing_amount",
  pacingInterval: "strategy_pacing_interval",
  pacingType: "strategy_pacing_type",
  postClickConversions: "post_click_conversions",
  postClickRevenue: "post_click_revenue",
  postClickTotalSpendRoi: "post_click_total_spend_roi",
  postViewConversions: "post_view_conversions",
  postViewRevenue: "post_view_revenue",
  postViewTotalSpendRoi: "post_view_total_spend_roi",
  potentialSpend14days: "potential_spend_14_days",
  potentialSpend14daysUncapped: "potential_spend_14_days_uncapped",
  potentialSpend30days: "potential_spend_30_days",
  potentialSpend30daysUncapped: "potential_spend_30_days_uncapped",
  potentialSpend7days: "potential_spend_7_days",
  potentialSpend7daysUncapped: "potential_spend_7_days_uncapped",
  potentialSpendToday: "potential_spend_today",
  potentialSpendTodayUncapped: "potential_spend_today_uncapped",
  potentialSpendYesterday: "potential_spend_yesterday",
  potentialSpendYesterdayUncapped: "potential_spend_yesterday_uncapped",
  remainingBudget: "strategy_flight_pacing_remaining",
  startDate: "strategy_start_date",
  status: "strategy_status",
  strategyDailyPacingSpendToPace: "strategy_daily_pacing_spend_to_pace",
  strategySpendIndicatorStatus: "strategy_spend_indicator_status",
  totalBudget: "strategy_budget",
  totalConversions: "total_conversions",
  totalRevenue: "total_revenue",
  totalSpend: "total_spend",
  totalSpendCpa: "total_spend_cpa",
  totalSpendCpc: "total_spend_cpc",
  totalSpendCpm: "total_spend_cpm",
  totalSpendRoi: "total_spend_roi",
  totalSpendVcpm: "total_spend_vcpm",
  totalSpendYesterday: "total_spend_yesterday",
  videoCompleteRate: "video_complete_rate",
  videoCompleteRateImpressionBased: "video_complete_rate_impression_based",
  videoFirstQuartileRate: "video_first_quartile_rate",
  videoMidpointRate: "video_midpoint_rate",
  videoPlayRate: "video_play_rate",
  videoThirdQuartileRate: "video_third_quartile_rate",
  viewabilityRate: "viewability_rate",
  viewabilityRate_100Percent: "viewability_rate_100_percent",
  winRate14Days: "win_rate_14_days",
  winRate30Days: "win_rate_30_days",
  winRate7Days: "win_rate_7_days",
  winRateToday: "win_rate_today",
  winRateYesterday: "win_rate_yesterday",
};

export const numericOperators = {
  0: ">",
  1: "<",
  2: "=",
  3: "=~",
};

/*
  Maps each Campaign Status filter option to the reporting field and value it
  filters on. Options sharing a reporting field (e.g. live/new/idle) can be
  combined in a multi-select and sent as a single IN-style array filter; the
  reporting API cannot OR across different fields, so the UI disables
  cross-field combinations (see isCampaignStatusOptionDisabled).

  NOTE: FILTER_STATUS_NEW could be either 0 or null, however, filtering by
  null isn't supported at this time. Realistically, there shouldn't be any
  null values to begin with.
*/
const campaignStatusFilterFields = {
  [FILTER_STATUS_ACTIVE]: { field: mapFields.campaignStatus, value: true },
  [FILTER_STATUS_ARCHIVED]: { field: mapFields.campaignArchived, value: true },
  [FILTER_STATUS_IDLE]: {
    field: mapFields.campaignSpendIndicatorStatus,
    value: 2,
  },
  [FILTER_STATUS_INACTIVE]: { field: mapFields.campaignStatus, value: false },
  [FILTER_STATUS_LIVE]: {
    field: mapFields.campaignSpendIndicatorStatus,
    value: 1,
  },
  [FILTER_STATUS_NEW]: {
    field: mapFields.campaignSpendIndicatorStatus,
    value: 0,
  },
  [FILTER_STATUS_RUNNING]: { field: mapFields.campaignInFlight, value: true },
};

// Filters created before the status filter became a multi-select may still
// hold a single cached string value, hence the normalization.
export const toStatusFilterValues = (value) => {
  if (isNil(value)) return [];
  return Array.isArray(value) ? value : [value];
};

export const isCampaignStatusOptionDisabled = (
  optionValue,
  selectedValues = []
) => {
  if (!selectedValues.length) return false;
  const [firstSelected] = selectedValues;
  const selectedField = campaignStatusFilterFields[firstSelected]?.field;

  // An unknown selection (e.g. a stale cached value) maps to no reporting
  // field and must not disable anything, or the user could never recover
  if (!selectedField) return false;

  return campaignStatusFilterFields[optionValue]?.field !== selectedField;
};

const parseCampaignStatusFilter = (value) => {
  const valuesByField = {};

  for (const statusValue of toStatusFilterValues(value)) {
    const mappedStatus = campaignStatusFilterFields[statusValue];

    if (mappedStatus) {
      if (!valuesByField[mappedStatus.field]) {
        valuesByField[mappedStatus.field] = [];
      }
      valuesByField[mappedStatus.field].push(mappedStatus.value);
    }
  }

  // A single status stays scalar to keep the request identical to the old
  // single-select; multiple same-field statuses become an IN-style array
  // value, which the reporting API supports for numeric fields only.
  return Object.entries(valuesByField).reduce((acc, [field, values]) => {
    // Active + Inactive cover every campaign_status value, and the reporting
    // API cannot match boolean arrays — omitting the filter is equivalent
    const isBooleanPair =
      values.length > 1 &&
      values.every((fieldValue) => typeof fieldValue === "boolean");

    if (values.length === 1) {
      acc.push({ field, op: numericOperators[2], value: values[0] });
    } else if (!isBooleanPair) {
      acc.push({ field, op: numericOperators[2], value: values });
    }

    return acc;
  }, []);
};

export const parseFilters = ({ filter }) => {
  return (
    Object.keys(filter)
      .map((key) => {
        const { operator, type, value } = filter[key];
        const isCampaignStatus = key === "campaignStatus";
        const isStrategyStatus = key === "status";
        const equals = numericOperators[2];

        switch (type) {
          case "dropdown":
            if (isCampaignStatus) {
              return parseCampaignStatusFilter(value);
            }

            if (isStrategyStatus && value === FILTER_STATUS_RUNNING) {
              const field = mapFields.strategyInFlight;
              return { field, op: equals, value: true };
            }
            if (isStrategyStatus && value === FILTER_STATUS_LIVE) {
              const field = mapFields.strategySpendIndicatorStatus;
              return { field, op: equals, value: 1 };
            }
            if (isStrategyStatus && value === FILTER_STATUS_NEW) {
              const field = mapFields.strategySpendIndicatorStatus;
              /*
                NOTE: FILTER_STATUS_NEW could be either 0 or null, however,
                filtering by null isn't supported at this time. Realistically,
                there shouldn't be any null values to begin with.
              */
              return { field, op: equals, value: 0 };
            }
            if (isStrategyStatus && value === FILTER_STATUS_IDLE) {
              const field = mapFields.strategySpendIndicatorStatus;
              return { field, op: equals, value: 2 };
            }
            if (isStrategyStatus && value === FILTER_STATUS_ARCHIVED) {
              const field = mapFields.strategyArchived;
              return { field, op: equals, value: true };
            }

            if (isStrategyStatus) {
              const isActive = value === FILTER_STATUS_ACTIVE;
              return { field: mapFields[key], op: equals, value: isActive };
            }

            return { field: mapFields[key], op: equals, value };
          case "string":
            return {
              op: numericOperators[3],
              field: mapFields[key],
              value,
            };
          case "date":
            if (value)
              return {
                op: numericOperators[operator],
                field: mapFields[key],
                value,
              };
            return {};
          case "numeric":
            return {
              op: numericOperators[operator],
              field: mapFields[key],
              value,
            };
          case FAVORITE_CAMPAIGN:
            return {
              op: equals,
              field: mapFields[key],
              value,
            };
          default:
            return {};
        }
      })
      // The campaign status filter may expand to multiple filter objects
      .flat()
      .filter((item) => {
        return !isEmpty(item);
      })
  );
};

export const strategyOps = {
  0: "gt",
  1: "ls",
  2: "eq",
};

export const parseStrategyFilters = ({ filter = {} }) => {
  return Object.keys(filter).map((key) => {
    const filterItem = filter[key];
    const { type, value, operator } = filterItem;
    switch (type) {
      case "dropdown":
        if (key === "status" && value === "running") {
          return {
            operator: strategyOps[2],
            property: key, // do not map status as strategy_status is solely a Reporting field
            value: true,
          };
        }
        if (key === "status" && value === FILTER_STATUS_ARCHIVED) {
          return {
            operator: numericOperators[2],
            property: key,
            value: true,
          };
        }
        if (key === "status") {
          return {
            operator: strategyOps[2],
            property: key,
            value: value === "active",
          };
        }
        return {
          operator: strategyOps[2],
          property: mapFields[key],
          value,
        };
      case "string":
        return {
          operator: strategyOps[2],
          property: mapFields[key],
          value,
        };
      case "date":
        return {
          operator: strategyOps[operator],
          property: mapFields[key],
          value,
        };
      case "numeric":
        return {
          operator: strategyOps[operator],
          property: mapFields[key],
          value,
        };
      default:
        return {};
    }
  });
};
